import React from 'react'

import PersonIcon from '@mui/icons-material/Person'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { format } from 'date-fns'
import type { FeatureChange } from 'features/customers/hooks/useCustomerFeatureChanges'
import { Link } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { EChangeSource } from '@albacross/types'

import { Dash } from 'components/_base'

export const actorName = (change: FeatureChange) => {
  const { setBy } = change
  const name = [setBy?.first_name, setBy?.last_name].filter(Boolean).join(' ')

  if (name) return name

  if (setBy) return `User ${setBy.id}`

  return 'Someone'
}

// Reconstructed history carries whatever date the revision had, and legacy customer_features rows
// hold MySQL zero dates. format() throws RangeError on those rather than printing anything, so an
// unreadable date has to be caught here - and null must not silently become 1 Jan 1970.
const changedAt = (change: FeatureChange) => {
  if (!change.changed_at) return undefined

  const at = new Date(change.changed_at)

  return Number.isNaN(at.getTime()) ? undefined : at
}

const NO_DATE = 'date unknown'

const on = (change: FeatureChange, pattern: string) => {
  const at = changedAt(change)

  return at ? format(at, pattern) : NO_DATE
}

export const changedAtLabel = (change: FeatureChange) => on(change, 'd MMM yy, HH:mm')

// The row has no space for the time, and the history below shows it in full.
const changedOnLabel = (change: FeatureChange) => on(change, 'd MMM yy')

// To the second, which is all changed_at holds: it is a MySQL DATETIME, so there are no fractional
// seconds to show and printing .000 would be precision the column does not have.
const exactlyWhen = (change: FeatureChange) => on(change, 'd MMM yyyy, HH:mm:ss')

// What you want when you find a change you did not expect: exactly when, and the reason they gave.
// Not the email - knowing who it was is the chip's job, and the chip links to them.
const actorTooltip = (change: FeatureChange, departed?: boolean) => (
  <>
    {/* The chip truncates a long name, so this is the only place the whole of it survives. */}
    <Box sx={{ fontWeight: 600 }}>{actorName(change)}</Box>
    <Box>{exactlyWhen(change)}</Box>
    <Box sx={{ mt: 0.5 }}>{change.note ? change.note : 'No reason given'}</Box>
    {departed && <Box sx={{ mt: 0.5, opacity: 0.75 }}>Their account is no longer on file</Box>}
  </>
)

const TRUNCATE = { maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', minWidth: 0 } } as const

// The name is the part with no upper bound, so it is the part that truncates. The date is short and
// fixed, and it was the half being cut when the whole label ellipsised together.
const ChipLabel = ({ text, when }: { text: string; when?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {text}
    </Box>
    {when && (
      <Box component="span" sx={{ flexShrink: 0, opacity: 0.75 }}>
        · {when}
      </Box>
    )}
  </Box>
)

type Props = { change?: FeatureChange; overridden?: boolean; hideDate?: boolean }

const CustomerFeatureSource = ({ change, overridden, hideDate }: Props) => {
  // Overridden with no record is a real state: stamping is fail-open, and nothing predating the log
  // has a row. Unknown actor, not absent.
  if (!change) {
    if (!overridden) {
      return <Dash />
    }

    return (
      <Tooltip title="Something set this and no record of it exists. A save that failed to record looks like this.">
        <Typography color="text.disabled" component="span" variant="body2">
          not recorded
        </Typography>
      </Tooltip>
    )
  }

  const when = hideDate ? undefined : changedOnLabel(change)
  const withWhen = (label: string) => <ChipLabel text={label} when={when} />

  if (change.source === EChangeSource.Admin) {
    const label = withWhen(actorName(change))

    // set_by_user_id survives the soft delete, so the link still resolves for someone who has left.
    if (change.set_by_user_id) {
      return (
        <Tooltip title={actorTooltip(change)}>
          <Chip
            clickable
            color="primary"
            component={Link}
            icon={<PersonIcon />}
            label={label}
            size="small"
            sx={TRUNCATE}
            to={UrlProvider.getUrl('user', { id: change.set_by_user_id })}
            variant="outlined"
          />
        </Tooltip>
      )
    }

    return (
      <Tooltip title={actorTooltip(change, true)}>
        <Chip color="primary" icon={<PersonIcon />} label={label} size="small" sx={TRUNCATE} variant="outlined" />
      </Tooltip>
    )
  }

  if (change.source === EChangeSource.Trial) {
    return (
      <Tooltip title="Set automatically when this customer started a trial">
        <Chip icon={<ScheduleIcon />} label={withWhen('Trial')} size="small" sx={TRUNCATE} variant="outlined" />
      </Tooltip>
    )
  }

  const isBackfilled = change.source === EChangeSource.Unknown

  const explanation = isBackfilled
    ? "Reconstructed from this customer's saved history, from before we recorded who changes what. The date is the save that made the change; the person who made it was never recorded."
    : 'Changed automatically rather than by a person — usually a plan or subscription update.'

  return (
    <Tooltip title={explanation}>
      <Chip
        icon={<SettingsSuggestIcon />}
        label={withWhen(isBackfilled ? 'Unknown' : 'System')}
        size="small"
        sx={{ ...TRUNCATE, color: 'text.secondary' }}
        variant="outlined"
      />
    </Tooltip>
  )
}

export default CustomerFeatureSource
